import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCheck, CircleAlert, Info, Trash2, X } from 'lucide-react'
import './ToastCard.scss'

/** Wozu der Toast da ist — bestimmt das Zeichen und seine Farbe. */
export type ToastKind = 'success' | 'undo' | 'error' | 'info'
/**
 * Die Farbe des Zeichens. Jedes Zeichen hat eine (Sven, 04.10.2026: Weiß war zu eintönig);
 * `neutral` — ein gereichtes Zeichen ohne eigenen Ton — nimmt den hellen Ton des Lebensbereichs.
 */
export type ToastTone = 'success' | 'danger' | 'recurring' | 'info' | 'favorite' | 'rest' | 'archive' | 'neutral'

export interface ToastCardAction {
  label: string
  onClick: () => void | Promise<void>
}

export interface ToastCardProps {
  kind: ToastKind
  title: string
  /** Zweite Zeile — nur wo sie etwas sagt (Sven, 04.10.2026). */
  description?: string
  /** Ersetzt das Zeichen der Art. */
  icon?: ReactNode
  /** Vorgabe: aus der Art; mit eigenem `icon` neutral. */
  tone?: ToastTone
  action?: ToastCardAction
  /**
   * Ein zweiter Knopf, VOR `action` („Anzeigen · Rückgängig 6 s“, #259). Der Countdown bleibt bei
   * `action`; `focusAction` legt den Fokus hierher.
   */
  secondaryAction?: ToastCardAction
  /** Sekunden bis zum Verschwinden, am Knopf („Rückgängig 5 s“). */
  secondsLeft?: number
  /**
   * Der erste Knopf nimmt beim Erscheinen den Fokus, Enter löst ihn aus, Esc schließt. Geht der Toast, kommt
   * der Fokus dorthin zurück, wo er vorher stand. Nur wo der Nutzer den Toast gerade selbst ausgelöst
   * hat (Schnelleingabe, Sven 04.10.2026; Verschieben, #264) — sonst löste Enter ihn mitten in der Liste aus.
   * Der erste ist bei zwei Knöpfen `secondaryAction`: Enter auf „Rückgängig“ nähme die Handlung zurück.
   */
  focusAction?: boolean
  /**
   * Der Fokus im Toast hält seine Zeit nicht an (`AppToaster`) — der Zeiger darüber schon. Für Toasts, die
   * sich den Fokus selbst nehmen und trotzdem gehen sollen (Verschieben, Sven 05.10.2026).
   */
  runsWhileFocused?: boolean
  /** Der Toast geht gerade (blendet aus, steht noch im DOM) — ein Fokus von `focusAction` geht schon jetzt zurück. */
  leaving?: boolean
  onDismiss: () => void
}

const KIND_ICON = { success: CheckCheck, undo: Trash2, error: CircleAlert, info: Info } as const
const KIND_TONE: Record<ToastKind, ToastTone> = { success: 'success', undo: 'danger', error: 'danger', info: 'info' }

/**
 * **Ein Toast** (#223, Prototyp `tools/toast-layout-prototype`, Variante F): Zeichen links, Titel,
 * darunter optional ein Text und der Knopf, ✕ oben rechts. Die Fläche ist das Milchglas des
 * Aufgaben-Menüs im Ton des Lebensbereichs. Nur der Inhalt — Lage, Stapel und Zeit macht
 * `AppToaster`, die Aufrufe gehen über `showSuccessToast` & Co.
 *
 * @example
 * <ToastCard kind="undo" title="„Regal bauen“ gelöscht" description="Mit 3 Unteraufgaben."
 *   action={{ label: 'Rückgängig', onClick: undo }} secondsLeft={5} onDismiss={close} />
 *
 * @example
 * <ToastCard kind="undo" title="Nach Umzug verschoben" icon={<FolderInput size={18} />}
 *   secondaryAction={{ label: 'Anzeigen', onClick: show }}
 *   action={{ label: 'Rückgängig', onClick: undo }} secondsLeft={6} onDismiss={close} />
 */
export function ToastCard({ kind, title, description, icon, tone, action, secondaryAction, secondsLeft, focusAction, runsWhileFocused, leaving, onDismiss }: ToastCardProps) {
  const { t } = useTranslation()
  const Icon = KIND_ICON[kind]
  const resolvedTone = tone ?? (icon ? 'neutral' : KIND_TONE[kind])
  const cardRef = useRef<HTMLDivElement>(null)
  const actionRef = useRef<HTMLButtonElement>(null)
  const secondaryRef = useRef<HTMLButtonElement>(null)
  const returnFocus = useRef<(() => void) | null>(null)

  /*
    Layout-Effekt: Sein Aufräumen läuft, solange der Knopf noch im DOM steht — danach stünde der
    Fokus schon auf `body`, und man wüsste nicht mehr, ob er im Toast war.
  */
  useLayoutEffect(() => {
    const button = secondaryRef.current ?? actionRef.current
    if (!focusAction || !button) return
    const previous = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null
    button.focus({ preventScroll: true })
    const release = () => {
      returnFocus.current = null
      // Hat der Nutzer den Fokus inzwischen woanders hingesetzt, bleibt er dort.
      if (!cardRef.current?.contains(document.activeElement)) return
      // Ein inzwischen ausgeblendetes Feld (der geschlossene Web-Dialog der Schnelleingabe) bekommt ihn nicht.
      if (previous?.isConnected && previous.checkVisibility?.() !== false) previous.focus({ preventScroll: true })
      else (document.activeElement as HTMLElement | null)?.blur()
    }
    returnFocus.current = release
    return release
  }, [focusAction])

  /*
    Läuft die Zeit trotz Fokus ab (`runsWhileFocused`), träfe Enter sonst einen Knopf, der gerade verschwindet.
    Ein eigener Effekt statt eines Aufräumers: Was ein Aufräumer am Fokus ändert, stellt React nach dem
    Commit wieder her, solange der alte Knopf noch im DOM steht.
  */
  useLayoutEffect(() => {
    if (leaving) returnFocus.current?.()
  }, [leaving])

  const dismiss = () => {
    returnFocus.current?.()
    onDismiss()
  }
  const handleActionKeyDown = (e: KeyboardEvent) => {
    if (!focusAction) return
    // Der Knopf hat die Taste — die Liste dahinter soll Enter nicht auch noch als „Aufgabe öffnen“ lesen.
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }

  return (
    <div ref={cardRef} className={`toast-card toast-card--${resolvedTone}`} data-runs-while-focused={runsWhileFocused || undefined}>
      <span className="toast-card__icon" aria-hidden="true">{icon ?? <Icon size={18} />}</span>
      <div className="toast-card__body">
        <p className="toast-card__title">{title}</p>
        {description && <p className="toast-card__description">{description}</p>}
        {(action || secondaryAction) && (
          <div className="toast-card__actions">
            {secondaryAction && (
              <button
                ref={secondaryRef}
                type="button"
                className="toast-card__action"
                onKeyDown={handleActionKeyDown}
                onClick={() => { returnFocus.current?.(); void secondaryAction.onClick() }}
              >
                {secondaryAction.label}
              </button>
            )}
            {action && (
              <button
                ref={actionRef}
                type="button"
                className="toast-card__action"
                onKeyDown={handleActionKeyDown}
                // Erst den Fokus zurück, dann die Handlung — ein Sprung setzt ihn danach selbst.
                onClick={() => { returnFocus.current?.(); void action.onClick() }}
              >
                {action.label}
                {secondsLeft != null && (
                  // Nicht vorlesen: Der Toast ist eine Live-Region, sonst sagte der Screenreader jede Sekunde an.
                  <span className="toast-card__countdown" aria-hidden="true">{t('toast.countdown', { count: secondsLeft })}</span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
      <button type="button" className="toast-card__close" aria-label={t('toast.close')} onClick={dismiss}>
        <X size={15} />
      </button>
    </div>
  )
}
